import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Text,
  Flex,
  Button,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { Toggle } from 'rsuite';
import { useFeatureFlagFromContext } from '../../../launchdarkly';

/**
 * Budget Settings Modal Component
 */
const BudgetSettingsModal = ({
  isOpen,
  onClose,
  onSave,
  onCancel,
  settings,
  setSettings,
  saveBtnLoading,
}) => {
  const {
    enableBudgeting,
    locationClassLevelBudgeting,
    yearBasedBudgeting,
    propotionalBudgeting,
  } = settings;

  // Check if the showReskin feature flag is enabled
  const showReskin = useFeatureFlagFromContext('showReskin', false);

  // State to track if settings have been changed
  const [hasChanges, setHasChanges] = useState(false);

  // Reset hasChanges when modal is opened/closed
  useEffect(() => {
    setHasChanges(false);
  }, [isOpen]);

  // Wrapper for setSettings that also tracks changes
  const handleSettingsChange = newSettings => {
    setSettings(newSettings);
    setHasChanges(true);
  };

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      size={'lg'}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center" fontSize="lg" fontWeight="bold">
          Budgeting Settings
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Warning message for reskinned version */}
          {showReskin && hasChanges && (
            <Alert status="warning" mb={4}>
              <AlertIcon />
              <Text fontSize="sm">
                Warning: All the previous data maintained will be deleted when
                you save these changes.
              </Text>
            </Alert>
          )}
          <Flex justifyContent={'center'} wrap={'wrap'}>
            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'flex-end'} marginTop={2}>
                  <Text fontSize={'xs'}>Enable Budgeting</Text>
                </FormLabel>
                <Toggle
                  size="xs"
                  style={{ marginTop: 4 }}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  defaultChecked={enableBudgeting}
                  onChange={e => {
                    if (e === false) {
                      handleSettingsChange({
                        ...settings,
                        enableBudgeting: e,
                        yearBasedBudgeting: false,
                        locationClassLevelBudgeting: false,
                        propotionalBudgeting: false,
                      });
                    } else {
                      handleSettingsChange({
                        ...settings,
                        enableBudgeting: e,
                      });
                    }
                  }}
                />
              </Flex>
            </FormControl>
            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'flex-end'} marginTop={2}>
                  <Text fontSize={'xs'}>
                    Configure Budgeting on Location / Class Level
                  </Text>
                </FormLabel>
                <Toggle
                  size="xs"
                  style={{ marginTop: 4 }}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  defaultChecked={locationClassLevelBudgeting}
                  checked={locationClassLevelBudgeting}
                  disabled={!enableBudgeting}
                  onChange={e => {
                    handleSettingsChange({
                      ...settings,
                      locationClassLevelBudgeting: e,
                    });
                  }}
                />
              </Flex>
            </FormControl>

            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'flex-end'} marginTop={2}>
                  <Text fontSize={'xs'}>Inputs will be on Yearly Basis ?</Text>
                </FormLabel>
                <Toggle
                  size="xs"
                  style={{ marginTop: 4 }}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  defaultChecked={yearBasedBudgeting}
                  checked={yearBasedBudgeting}
                  disabled={!enableBudgeting}
                  onChange={e => {
                    if (e === false) {
                      handleSettingsChange({
                        ...settings,
                        yearBasedBudgeting: e,
                        propotionalBudgeting: false,
                      });
                    } else {
                      handleSettingsChange({
                        ...settings,
                        yearBasedBudgeting: e,
                      });
                    }
                  }}
                />
              </Flex>
            </FormControl>

            <FormControl>
              <Flex align={{ base: 'center', md: 'start' }}>
                <FormLabel alignItems={'flex-end'} marginTop={2}>
                  <Text fontSize={'xs'}>Divide into Equal Proportions?</Text>
                </FormLabel>
                <Toggle
                  size="xs"
                  style={{ marginTop: 4 }}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  defaultChecked={propotionalBudgeting}
                  checked={propotionalBudgeting}
                  disabled={!enableBudgeting || !yearBasedBudgeting}
                  onChange={e => {
                    handleSettingsChange({
                      ...settings,
                      propotionalBudgeting: e,
                    });
                  }}
                />
              </Flex>
            </FormControl>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex w="100%" justify="space-between" gap={4}>
            <Button
              colorScheme="blue"
              onClick={onSave}
              isLoading={saveBtnLoading}
              flex={1}
              height="40px"
            >
              Save
            </Button>
            <Button
              onClick={onCancel}
              flex={1}
              height="40px"
              style={{ marginTop: '0px' }}
            >
              Cancel
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BudgetSettingsModal;
